import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  APPLICATION_MODULES,
  APPLICATION_REQUEST_STATUSES,
  labelFor,
  toneFor,
} from "const/referralEnums";
import {
  groupedVisaOptions,
  insurance_companies,
  insurance_cover_type,
  states,
} from "const/constants";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  fetchPartnerApplicationHistory,
  fetchPartnerApplicationRequests,
  fetchPartnerApplications,
  updatePartnerApplicationRequest,
} from "../../api/endpoints/applications";
import { fetchPartnerCourses } from "../../api/endpoints/workspace";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { formatDate, formatDateTime } from "../../utils/portalHelpers";
import { EmptyState, Panel, StatusChip, chipTone } from "../../components/ui";

/** Insurance types mirror INSURANCE_TYPE_OPTIONS in the admin insurance form. */
const INSURANCE_TYPES = [
  { label: "OSHC", value: "OSHC" },
  { label: "OVHC", value: "OVHC" },
];

/** Common extra services a partner can ask for; anything else is typed in freely. */
const OTHER_SERVICES = [
  { label: "Tax Service", value: "Tax Service" },
  { label: "Home Loan", value: "Home Loan" },
  { label: "Travel & Tours", value: "Travel & Tours" },
  { label: "Real Estate", value: "Real Estate" },
];

/**
 * Optional known-details fields per service. Anything the admin form offers as a dropdown is a
 * searchable dropdown here too (free text still allowed); anything free-text in admin stays
 * free-text. Only the service itself is required.
 */
const DETAIL_FIELDS = {
  admission: [
    { key: "courseName", label: "Course (if known)", type: "course" },
    { key: "universityName", label: "University / college (if known)", type: "text" },
    { key: "intake", label: "Intake (if known)", type: "intake" },
    { key: "state", label: "State (if known)", type: "state" },
  ],
  visa: [{ key: "visa", label: "Applied visa (if known)", type: "visa" }],
  insurance: [
    {
      key: "insuranceCompany",
      label: "Insurer (if known)",
      type: "options",
      options: insurance_companies,
    },
    {
      key: "policyType",
      label: "Insurance type — OSHC or OVHC (if known)",
      type: "options",
      options: INSURANCE_TYPES,
    },
    {
      key: "coverType",
      label: "Cover type (if known)",
      type: "options",
      options: insurance_cover_type,
    },
    { key: "visa", label: "Visa subclass (if known)", type: "text" },
  ],
  "skill-assessment": [
    { key: "occupation", label: "Occupation (if known)", type: "text" },
    { key: "courseProvider", label: "Provider (if known)", type: "text" },
  ],
  // USI is deliberately not asked: the admin RPL form does not ask for it either, and the office
  // collects it from the client if ever needed.
  "rpl-course": [{ key: "certificate", label: "Certificate (if known)", type: "text" }],
  other: [
    {
      key: "otherName",
      label: "What is this for?",
      type: "options",
      options: OTHER_SERVICES,
      required: true,
    },
  ],
};

const labelOf = (option) => option?.label ?? option?.name ?? option?.value ?? "";

const valueOf = (option) => option?.value ?? option?.name ?? option ?? "";

/**
 * Searchable dropdown that also accepts free text, mirroring the admin Autocompletes. The stored
 * value is always the plain string the office reads.
 */
const SuggestField = ({ label, value, options, groupBy, required, onChange }) => (
  <Autocomplete
    freeSolo
    options={options}
    getOptionLabel={(option) => (typeof option === "string" ? option : labelOf(option))}
    groupBy={groupBy}
    value={value || ""}
    onChange={(_, next) => onChange(next ? valueOf(next) : "")}
    onInputChange={(_, next, reason) => {
      if (reason === "input") onChange(next);
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        size="small"
        required={Boolean(required)}
        sx={{ mb: 2 }}
      />
    )}
  />
);

const CourseField = ({ details, onPick, onText }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  const { data, isFetching } = usePartnerQuery(
    partnerQueryKeys.courses({ search: debouncedSearch }),
    () => fetchPartnerCourses({ search: debouncedSearch || undefined, page: 0, size: 20 }),
    { enabled: open }
  );
  const options = data?.content ?? [];

  return (
    <Autocomplete
      freeSolo
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={isFetching}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : `${option.courseName} — ${option.universityName}`
      }
      value={details.courseName || ""}
      onChange={(_, next) => {
        if (next && typeof next !== "string") onPick(next);
        else if (!next) onText("");
      }}
      onInputChange={(_, next, reason) => {
        if (reason === "input") {
          setSearch(next);
          onText(next);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Course (if known)"
          size="small"
          sx={{ mb: 2 }}
          helperText="Pick a course to fill university, intake and state options"
        />
      )}
    />
  );
};

const HistoryTimeline = ({ clientId, module, entryId }) => {
  const { data, isLoading } = usePartnerQuery(
    partnerQueryKeys.applicationHistory(clientId, module, entryId),
    () => fetchPartnerApplicationHistory(clientId, module, entryId),
    { enabled: Boolean(clientId) && Boolean(entryId) }
  );
  const entries = Array.isArray(data) ? data : [];
  if (isLoading) return <p className="text-xs text-slate-500 m-0 mt-2">Loading history…</p>;
  if (entries.length === 0)
    return <p className="text-xs text-slate-500 m-0 mt-2">No status changes recorded yet.</p>;
  return (
    <ol className="list-none p-0 m-0 mt-2 border-l-2 border-teal-100 ml-1">
      {entries.map((entry, index) => (
        <li key={entry.id ?? index} className="relative pl-4 pb-3 last:pb-0">
          <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-teal-500" />
          <p className="text-xs font-bold text-slate-900 m-0">
            {entry.label || entry.toStatus || "Update"}
          </p>
          {entry.text ? (
            <p className="text-xs text-slate-600 m-0 mt-0.5 whitespace-pre-wrap">{entry.text}</p>
          ) : null}
          <p className="text-[11px] text-slate-400 m-0 mt-0.5">
            {entry.actorName ? `${entry.actorName} · ` : ""}
            {formatDateTime(entry.at)}
          </p>
        </li>
      ))}
    </ol>
  );
};

/**
 * The new-application form. `clientSlot` renders above it when the client is chosen in the form
 * itself (the dashboard), and `clientMissing` stops a send until one is picked.
 */
export const RequestDialog = ({
  open,
  initial,
  clientName,
  onClose,
  onSaved,
  saving,
  error,
  clientSlot = null,
  clientMissing = false,
}) => {
  const [module, setModule] = useState("admission");
  const [note, setNote] = useState("");
  const [details, setDetails] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null);
  const fields = DETAIL_FIELDS[module] || [];

  useEffect(() => {
    if (open) {
      setModule(initial?.module || "admission");
      setNote(initial?.requestNote || "");
      setDetails(initial?.requestDetails || {});
      setSelectedCourse(null);
    }
    // Reset the form each time a request (or a fresh one) is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const setDetail = (key, value) =>
    setDetails((prev) => {
      if (!value) {
        const { [key]: _dropped, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });

  const pickCourse = (course) => {
    setSelectedCourse(course);
    setDetails((prev) => ({
      ...prev,
      courseName: course.courseName || "",
      universityName: course.universityName || "",
      courseId: course.id != null ? String(course.id) : undefined,
      state: course.state || prev.state || "",
    }));
  };

  const save = () => {
    if (clientMissing) {
      toast.warning("Choose the client this application is for");
      return;
    }
    if (!module) return;
    if (module === "other" && !(details.otherName || "").trim() && !note.trim()) {
      toast.warning("Tell us what this is for so the office can arrange it");
      return;
    }
    onSaved({ moduleType: module, requestNote: note.trim() || null, requestDetails: details });
  };

  const renderField = (field) => {
    if (field.type === "course") {
      return (
        <CourseField
          key={field.key}
          details={details}
          onPick={pickCourse}
          onText={(next) => {
            setSelectedCourse(null);
            setDetail("courseName", next);
            setDetail("courseId", "");
          }}
        />
      );
    }
    if (field.type === "intake") {
      return (
        <SuggestField
          key={field.key}
          label={field.label}
          value={details[field.key] || ""}
          options={selectedCourse?.intakes ?? []}
          onChange={(next) => setDetail(field.key, next)}
        />
      );
    }
    if (field.type === "state") {
      return (
        <SuggestField
          key={field.key}
          label={field.label}
          value={details[field.key] || ""}
          options={states}
          onChange={(next) => setDetail(field.key, next)}
        />
      );
    }
    if (field.type === "visa") {
      return (
        <SuggestField
          key={field.key}
          label={field.label}
          value={
            groupedVisaOptions.find((option) => option.name === details[field.key]) ??
            details[field.key] ??
            ""
          }
          options={groupedVisaOptions}
          groupBy={(option) => option.group}
          onChange={(next) =>
            setDetail(field.key, typeof next === "string" ? next : next?.name ?? "")
          }
        />
      );
    }
    if (field.type === "options") {
      return (
        <SuggestField
          key={field.key}
          label={field.label}
          value={details[field.key] || ""}
          options={field.options}
          required={field.required}
          onChange={(next) => setDetail(field.key, next)}
        />
      );
    }
    return (
      <TextField
        key={field.key}
        fullWidth
        size="small"
        required={Boolean(field.required)}
        label={field.label}
        value={details[field.key] || ""}
        onChange={(event) => setDetail(field.key, event.target.value)}
        sx={{ mb: 2 }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial?.id ? "Edit and resubmit" : "Request a new application"}</DialogTitle>
      <DialogContent>
        {clientSlot ? <div className="mb-3">{clientSlot}</div> : null}
        <p className="text-xs text-slate-500 mt-0 mb-3">
          {clientSlot && !clientName
            ? "Pick the client, then the service."
            : `For ${clientName || "this client"}.`}{" "}
          Only the service is required — fill in whatever you know and the office completes the rest
          with the client.
        </p>
        <TextField
          select
          fullWidth
          size="small"
          label="Service"
          value={module}
          onChange={(event) => {
            setModule(event.target.value);
            setSelectedCourse(null);
          }}
          sx={{ mb: 2 }}
        >
          {Object.entries(APPLICATION_MODULES).map(([value, meta]) => (
            <MenuItem key={value} value={value}>
              {meta.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="What does the client need?"
          placeholder="e.g. Interested in admission for next intake, prefers Sydney"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          sx={{ mb: 2 }}
        />
        {fields.map(renderField)}
        {error ? <p className="text-xs text-rose-700 m-0">That did not save. Try again.</p> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving} sx={{ bgcolor: "#0f766e" }}>
          {saving ? "Sending…" : initial?.id ? "Resubmit for review" : "Send for review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// New applications start from the New application button at the top of the client page.
const ClientApplicationsTab = ({ clientId, clientName }) => {
  const queryClient = useQueryClient();
  const [historyFor, setHistoryFor] = useState(null);
  const [editing, setEditing] = useState(null);

  const {
    data: applications,
    isLoading,
    isError,
    refetch,
  } = usePartnerQuery(
    partnerQueryKeys.applications(clientId),
    () => fetchPartnerApplications(clientId),
    {
      enabled: Boolean(clientId),
    }
  );

  const requestParams = { clientId };
  const { data: requests } = usePartnerQuery(
    partnerQueryKeys.applicationRequests(requestParams),
    () => fetchPartnerApplicationRequests(requestParams),
    { enabled: Boolean(clientId) }
  );

  const invalidate = () => {
    queryClient.invalidateQueries(partnerQueryKeys.applications(clientId));
    queryClient.invalidateQueries(partnerQueryKeys.applicationRequests(requestParams));
  };

  const resubmit = useMutation((body) => updatePartnerApplicationRequest(editing.id, body), {
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  if (isLoading) {
    return <p className="text-sm text-slate-500">Loading applications…</p>;
  }

  if (isError) {
    return (
      <Panel title="Applications">
        <p className="text-sm text-slate-600 m-0">
          Applications could not be loaded.{" "}
          <button type="button" onClick={() => refetch()} className="text-teal-700 font-semibold">
            Try again
          </button>
        </p>
      </Panel>
    );
  }

  const rows = Array.isArray(applications) ? applications : [];
  const pending = (Array.isArray(requests) ? requests : []).filter(
    (row) => row?.status !== "APPROVED"
  );

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <EmptyState
          title="No applications shared yet"
          description="Admission, visa, insurance, skill assessment and RPL entries the office shares with you appear here with their full status history."
        />
      ) : (
        rows.map((app) => {
          const expanded = historyFor === app.id;
          return (
            <div
              key={`${app.module}-${app.id}`}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-700 m-0">
                    {labelFor(APPLICATION_MODULES, app.module)}
                  </p>
                  <p className="text-sm font-bold text-slate-900 m-0 mt-0.5">{app.title}</p>
                  {app.subtitle ? (
                    <p className="text-xs text-slate-500 m-0 mt-0.5">{app.subtitle}</p>
                  ) : null}
                  <p className="text-xs text-slate-500 m-0 mt-1">
                    Updated {formatDate(app.updatedAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusChip tone="neutral">{app.status || "In progress"}</StatusChip>
                  <button
                    type="button"
                    onClick={() => setHistoryFor(expanded ? null : app.id)}
                    className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700"
                  >
                    {expanded ? "Hide history" : "Status history"}
                  </button>
                </div>
              </div>
              {expanded ? (
                <HistoryTimeline clientId={clientId} module={app.module} entryId={app.id} />
              ) : null}
            </div>
          );
        })
      )}

      {pending.length > 0 ? (
        <Panel title="Your requests awaiting the office">
          <ul className="list-none p-0 m-0">
            {pending.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2.5 border-b border-slate-100 last:border-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 m-0">
                    {labelFor(APPLICATION_MODULES, row.module)}
                  </p>
                  {row.requestNote ? (
                    <p className="text-xs text-slate-500 m-0 mt-0.5">{row.requestNote}</p>
                  ) : null}
                  {row.status === "REJECTED" && row.adminNote ? (
                    <p className="text-xs text-rose-700 m-0 mt-0.5">Office: {row.adminNote}</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <StatusChip tone={chipTone(toneFor(APPLICATION_REQUEST_STATUSES, row.status))}>
                    {labelFor(APPLICATION_REQUEST_STATUSES, row.status)}
                  </StatusChip>
                  {row.status === "REJECTED" ? (
                    <button
                      type="button"
                      onClick={() => {
                        resubmit.reset();
                        setEditing(row);
                      }}
                      className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700"
                    >
                      Edit & resubmit
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <RequestDialog
        open={Boolean(editing)}
        initial={editing}
        clientName={clientName}
        onClose={() => setEditing(null)}
        onSaved={(body) => resubmit.mutate(body)}
        saving={resubmit.isLoading}
        error={resubmit.isError}
      />
    </div>
  );
};

export default ClientApplicationsTab;
